import React, { ReactElement } from 'react';
import BulkSelectorMenu from '../../widgets/BulkSelectorMenu';
import { AttributesInterface, Option } from '../../admin-dashboard-tables/interfaces';
import useScrollPage from '../../../../utils/hooks/useScrollPage';
import SimpleSortBy, { SortByProps } from '../../widgets/sorts/SimpleSortBy';
import SimpleSearchBox from './SimpleSearchBox';
import SellerAdsStateResults from './SellerAdsStateResults';
import SimpleResetSearch from './SimpleResetSearch';
import SimpleHitsPerPage from './SimpleHitsPerPage';
import SimplePagination from './SimplePagination';
import SimpleAttributeSelector from './SimpleAttributeSelector';

interface Props {
  bulkActions?: Array<Option>,
  setBulkAction?: (action: Option) => void,
  bulkAction?: Option,
  onQueryUpdateCallback?: (query: string) => void,
  ExternalBulkSelector?: React.JSXElementConstructor<any> | ReactElement,
  searchResults: {
    page: number,
    hitsPerPage: number,
    nbPages: number,
    nbHits: number,
  },
  query: string,
  attributes: Array<AttributesInterface>,
  onReset?: () => void,
  allowSetHitsPerPage?: boolean,
  customHits: JSX.Element,
  setHits?: (hits: Array<number>) => void,
  scrollPage?: () => void,
  clearBulkSelected?: () => void,
  onChangeHitsPerPage: (hitsPerPage: number) => void,
  setPage: (page: number) => void,
  sortByOptions?: SortByProps,
}

const WrapperlessSellerAdsTable: React.FC<Props> = ({
  query,
  bulkActions,
  setBulkAction,
  bulkAction,
  onQueryUpdateCallback,
  ExternalBulkSelector,
  onReset,
  allowSetHitsPerPage,
  customHits,
  searchResults,
  setHits,
  clearBulkSelected,
  scrollPage,
  onChangeHitsPerPage,
  setPage,
  attributes,
  sortByOptions,
}) => {
  if (!searchResults) {
    return <p>Loading...</p>;
  }
  return (
    <div className="admin-dashboard-table-wrapper">
      <div className="row d-flex justify-content-between align-items-center border-bottom border-l-gray pb-2 mb-4">
        <div className="col-sm-4 col-md-7 d-flex align-items-center flex-wrap justify-content-md-end">
          {(bulkActions && setBulkAction) && (
            <BulkSelectorMenu
              actions={bulkActions}
              setBulkAction={setBulkAction}
              bulkAction={bulkAction}
            />
          )}
        </div>
      </div>
      <div className="admin-filter-wrapper d-flex flex-wrap">
        {onQueryUpdateCallback && (
          <SimpleSearchBox
            placeholder="Type profile name or phone number"
            currentRefinement={query}
            onUpdateCallback={onQueryUpdateCallback}
          />
        )}
        {!!attributes && attributes.length > 0 && (
          attributes.map((attribute) => (
            <SimpleAttributeSelector
              key={attribute.attribute}
              attribute={attribute}
            />
          ))
        )}
        {ExternalBulkSelector}
      </div>
      <div className="admin-search-controls-container d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          {setHits && (
            <SellerAdsStateResults
              searchResults={searchResults}
              setHits={setHits}
              clearBulkSelected={clearBulkSelected}
            />
          )}
          {onReset && (
            <SimpleResetSearch
              onReset={onReset}
              disabled={false} // TODO: Add option to disable if not refined?
            />
          )}
        </div>
        <div className="search-controls-right d-flex flex-wrap">
          {allowSetHitsPerPage && (
            <div className="d-flex align-items-center hits-pp-wrapper">
              <p className="m-0 mr-2">Show</p>
              <SimpleHitsPerPage
                items={[
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                ]}
                currentRefinement={searchResults.hitsPerPage}
                onChange={onChangeHitsPerPage}
              />
            </div>
          )}
          {!!sortByOptions && (
            <div className="d-flex align-items-center hits-pp-wrapper">
              <p className="m-0 mr-2">Sort By</p>
              <SimpleSortBy {...sortByOptions} />
            </div>
          )}
        </div>
      </div>
      <>
        {customHits}
      </>
      <SimplePagination
        scrollPage={scrollPage || useScrollPage()}
        clearBulkSelected={clearBulkSelected}
        currentRefinement={searchResults.page + 1}
        nbPages={searchResults.nbPages}
        refine={setPage}
      />
    </div>
  );
};

export default WrapperlessSellerAdsTable;
